import * as THREE from 'three';
import { clamp, lerp } from '@/core/rng';

/**
 * Paths — repeated travel made visible (spec §43), and legible from the air.
 *
 * Each path is a worn-earth ribbon laid along the polyline the integrator
 * hands in. Render-only; the wear itself lives in the simulation, and this
 * module invents nothing about where anybody walked.
 *
 * Two things about a trail are decided here rather than in the simulation,
 * and both are about being *seen*:
 *
 * **A trail that has been walked for a century is a road.** Strength is the
 * simulation's own record of how much travel a line has carried — it opens at
 * 0.35, gains with every reuse, decays with disuse — and on the century-old
 * island 20 of the 33 ways are saturated, p50 0.97, p10 0.18
 * (`npx tsx tools/settlement.ts`, TRAILS block). A saturated
 * line gets three times the width of a new one, so the difference between a
 * trail somebody wore last season and the road between two old camps is
 * something you can see from the air rather than something in a data
 * structure.
 *
 * **And it holds a minimum apparent width.** A new trail is `WIDTH_NEW` =
 * 2.4 m of trodden earth, which on the shipped camera — 46°, and 1080 px is
 * the stage every probe in `tools/` measures on — is
 * `2.4 · 540 / (3000 · tan 23°) = 1.0 px` at three kilometres. One pixel is
 * genuinely invisible, which is why a century of accumulated travel read as
 * untouched wilderness from the altitude a god actually explores at, and it
 * is worse on a shorter window: the same trail is 0.85 px at 900 px of
 * height. Past a kilometre or so the ribbon
 * widens in step with its distance, which freezes how wide it looks. It is
 * the same render-only exaggeration the figures, the buildings and the smoke
 * already make: the simulation is never told, and PATH_SPEED still applies to
 * the line as walked. The widening is capped, so a road stays a road.
 *
 * Both live in the vertex shader, off `position` as the centreline and a
 * per-vertex sideways offset — so distance is resolved per vertex, exactly,
 * and a trail running away from the eye tapers along its own length instead
 * of picking one width for the whole ribbon.
 *
 * **And the ribbon now crosses the slope instead of lying on a shelf.** Both
 * of a rung's vertices took the centreline's `y`, which makes a 7.6 m road
 * horizontal across its own width: on a traverse one edge was buried in the
 * hillside and the other floated over it, and the distance-widened ribbon —
 * up to 24 m across at range — floated on one whole side of every cross-slope.
 * `render/route.ts` measures the ground's cross-slope where it seats each
 * point and carries it on the point as `cross`; the shader applies it at the
 * ribbon's true half-width, and the rung's normal is built from the same two
 * directions, so a path on a shaded hillside is now lit like the hillside
 * rather than like the sky.
 *
 * Geometry is rebuilt only when the caller bumps the version number. Strength
 * decays every sim day without a version bump, so every call re-syncs the
 * per-vertex strength attribute (matched by id, no allocation) — a fading
 * path visibly narrows and dims before it dissolves. Callers must pass current
 * strengths on every update, not a view array cached at the last version bump.
 */

export interface PathView {
  id: string;
  /**
   * The worn line, in world metres; y already resolved to the ground.
   *
   * `cross` — the ground's slope ACROSS the way, `dy/ds` along the ribbon's own
   * sideways axis — is what seats the ribbon on a hillside rather than on a
   * shelf. `render/route.ts` measures it at the same point it seats `y`, and it
   * is optional so that a caller who has not got one still draws a path (it
   * simply lies flat across, which is what every path did before).
   */
  points: Array<{ x: number; y: number; z: number; cross?: number }>;
  /** 0..1 — how deeply travel has cut this line into the land. */
  strength: number;
}

export interface Paths {
  group: THREE.Group;
  update(views: PathView[], version: number): void;
}

/** Metres across, at the strength a path forms with and at saturation. */
const WIDTH_NEW = 2.4, WIDTH_ROAD = 7.6;
/**
 * Metres of half-width per metre of distance, once distance is what decides.
 * At 1060 px per metre-at-one-metre (46° over 900 px) this holds a road at
 * about five pixels — thin, but a line, and lines are what the eye assembles
 * into a network.
 */
const HOLD = 0.0024;
/** Never wider than this many times its true width: a road stays a road. */
const MAX_GROW = 5;
/**
 * And never wider than this, in metres, however far off it is.
 *
 * Without it the network went on widening past the range at which anything
 * else in the frame is legible, and from nine kilometres a valley read as a
 * road atlas: every line the same crisp width, because the distance term had
 * swamped the strength term. This lets trails fall out of legibility somewhere
 * past six kilometres, which is honest — at that range a god should be reading
 * smoke, not footpaths.
 */
const MAX_HALF = 12;
/**
 * Lift off the terrain. The ribbon is a chord between samples ~64 m apart and
 * the ground between them is not flat, so at distance a trail used to break
 * into dashes where convex ground cut through it. The integrator samples the
 * line more finely than that now; this is the belt to that pair of braces, and
 * it costs nothing visible — four metres at three kilometres is one pixel.
 */
const LIFT = 0.12, LIFT_PER_M = 0.0014;
/**
 * Trodden soil. The same colour `world/wear.ts` packs a camp's ground toward,
 * because they are the same fact: a path is a camp's wear stretched thin. The
 * old value was three shades lighter and, after ACES at 1.55 exposure, came
 * out near-white — which read as scattered debris rather than as ground.
 */
const SOIL = new THREE.Color(0.30, 0.25, 0.18);
/** A new trail is half there; a road is nearly opaque earth. */
const ALPHA_BASE = 0.45, ALPHA_GAIN = 0.45;

const widthOf = (strength: number) => lerp(WIDTH_NEW, WIDTH_ROAD, clamp(strength, 0, 1));
const opacityOf = (strength: number) => clamp(ALPHA_BASE + strength * ALPHA_GAIN, 0, 1);

export function createPaths(): Paths {
  const group = new THREE.Group();

  /**
   * One mesh for the whole network. Fifty-six ribbons were fifty-six draw
   * calls and fifty-six materials, and at the range where the network is the
   * point every one of them is on screen at once. Per-path strength rides an
   * attribute instead, which is also what lets a single shader do the
   * widening.
   */
  const mat = new THREE.MeshStandardMaterial({
    color: SOIL, roughness: 1, metalness: 0,
    transparent: true, depthWrite: false, side: THREE.DoubleSide,
    // sit on the terrain without z-fighting it, whatever the LOD underfoot
    polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -2,
  });
  mat.onBeforeCompile = (sh) => {
    sh.uniforms.uHold = { value: HOLD };
    sh.uniforms.uGrow = { value: new THREE.Vector2(MAX_GROW, MAX_HALF) };
    sh.uniforms.uLift = { value: new THREE.Vector2(LIFT, LIFT_PER_M) };
    sh.vertexShader = sh.vertexShader
      .replace('#include <common>', `#include <common>
        attribute vec2 aSide;      // unit perpendicular, already signed
        attribute float aCross;    // dy/ds along THIS vertex's own aSide
        attribute float aStrength;
        attribute float aEdge;     // −1 on one edge of the ribbon, +1 on the other
        uniform float uHold;
        uniform vec2 uGrow;
        uniform vec2 uLift;
        varying float vStrength;
        varying float vEdge;
        varying vec3 vPathW;`)
      .replace('#include <begin_vertex>', `#include <begin_vertex>
        vStrength = aStrength;
        vEdge = aEdge;
        float use = clamp(aStrength, 0.0, 1.0);
        float halfW = mix(${WIDTH_NEW.toFixed(3)}, ${WIDTH_ROAD.toFixed(3)}, use) * 0.5;
        float d = distance(cameraPosition, (modelMatrix * vec4(transformed, 1.0)).xyz);
        // the distance term is scaled by use as well, or a footpath and a
        // century-old road end up the same width the moment distance takes
        // over, and the network loses the only hierarchy it has
        float held = d * uHold * mix(0.45, 1.0, use);
        float w = min(max(halfW, held), min(halfW * uGrow.x, uGrow.y));
        transformed.xz += aSide * w;
        // …and CROSS THE SLOPE with it. Both of a rung's vertices used to take
        // the centreline's y, so a 7.6 m road lay horizontally across its own
        // width: on a hillside one edge was buried and the other floated, and a
        // distance-widened ribbon floated on one side of every traverse. The
        // cross-fall is applied at the ribbon's TRUE half width and no further,
        // because everything past that is the legibility exaggeration above —
        // extrapolating a 30° hillside out to the 12 m cap would tilt a distant
        // trail by six metres to make it three pixels wider.
        transformed.y += aCross * min(w, halfW) + uLift.x + d * uLift.y;
        vPathW = (modelMatrix * vec4(transformed, 1.0)).xyz;`);
    sh.fragmentShader = sh.fragmentShader
      .replace('#include <common>', `#include <common>
      varying float vStrength; varying float vEdge; varying vec3 vPathW;
      float pHash(vec2 i){ vec3 q = fract(vec3(i.x, i.y, i.x) * 0.1031); q += dot(q, q.yzx + 33.33); return fract((q.x + q.y) * q.z); }
      float pNoise(vec2 p){ vec2 i = floor(p), f = p - i; vec2 u = f * f * (3.0 - 2.0 * f);
        return mix(mix(pHash(i), pHash(i + vec2(1.0, 0.0)), u.x), mix(pHash(i + vec2(0.0, 1.0)), pHash(i + vec2(1.0, 1.0)), u.x), u.y); }`)
      // A fading trail dims as well as narrows; a road is the darkest earth.
      // And the ribbon is WORN GROUND rather than a painted stripe
      // (2026-08-19): its edges feather into the turf over the outer third,
      // the middle is the palest — packed and dusty where the feet go — a
      // strong path carries two faint ruts at ±0.45 of its width, and a
      // world-anchored 1.6 m mottle breaks the even tone. Nothing here moves
      // a vertex; it is all in the alpha and the tint.
      .replace('#include <alphatest_fragment>', `
        float use = clamp(vStrength, 0.0, 1.0);
        float ax = abs(vEdge);
        float feather = 1.0 - smoothstep(0.62, 1.0, ax);
        float mottle = pNoise(vPathW.xz / 1.6) - 0.5;
        float worn = (1.0 - ax * ax) * (0.05 + 0.05 * use);
        float rut = smoothstep(0.10, 0.0, abs(ax - 0.45)) * use * 0.10;
        diffuseColor.rgb *= 1.0 + worn - rut + mottle * 0.12;
        diffuseColor.a *= clamp(${ALPHA_BASE.toFixed(3)} + vStrength * ${ALPHA_GAIN.toFixed(3)},
                                0.0, 1.0) * feather * (0.9 + 0.2 * mottle);
        #include <alphatest_fragment>`);
  };

  const mesh = new THREE.Mesh(new THREE.BufferGeometry(), mat);
  mesh.name = 'trails';
  // the shader moves vertices sideways and upward off the centreline, so the
  // geometry's own bounds understate it; there is one mesh, so culling it by
  // hand would save nothing worth the risk of losing a road at the frame edge
  mesh.frustumCulled = false;
  mesh.visible = false;
  group.add(mesh);

  /** Which stretch of the strength attribute belongs to which path. */
  const span = new Map<string, { at: number; n: number }>();
  let strengths: Float32Array | null = null;
  let seen: number | undefined;

  return {
    group,
    update(views, version) {
      if (version !== seen) {
        seen = version;
        span.clear();
        mesh.geometry.dispose();

        const usable = views.filter((v) => v.points.length >= 2);
        const verts = usable.reduce((s, v) => s + v.points.length * 2, 0);
        if (verts === 0) {
          mesh.geometry = new THREE.BufferGeometry();
          mesh.visible = false;
          strengths = null;
          return;
        }
        const pos = new Float32Array(verts * 3);
        const nrm = new Float32Array(verts * 3);
        const side = new Float32Array(verts * 2);
        const cross = new Float32Array(verts);
        const edge = new Float32Array(verts);
        strengths = new Float32Array(verts);
        const idx: number[] = [];
        let base = 0;

        for (const v of usable) {
          const pts = v.points;
          const P = pts.length;
          span.set(v.id, { at: base, n: P * 2 });
          for (let i = 0; i < P; i++) {
            // direction averaged over the neighbours, so corners mitre softly
            const a = pts[Math.max(0, i - 1)], b = pts[Math.min(P - 1, i + 1)];
            const dx = b.x - a.x, dz = b.z - a.z;
            const l = Math.hypot(dx, dz) || 1;
            const px = -dz / l, pz = dx / l;
            const p = pts[i], k = (base + i * 2) * 3, s = (base + i * 2) * 2;
            // both vertices carry the centreline; the shader spreads them
            pos[k] = p.x; pos[k + 1] = p.y; pos[k + 2] = p.z;
            pos[k + 3] = p.x; pos[k + 4] = p.y; pos[k + 5] = p.z;
            side[s] = px; side[s + 1] = pz;
            side[s + 2] = -px; side[s + 3] = -pz;
            edge[base + i * 2] = 1; edge[base + i * 2 + 1] = -1;
            const c = p.cross ?? 0;
            cross[base + i * 2] = c; cross[base + i * 2 + 1] = -c;
            /**
             * The rung's own normal, from the two directions it actually
             * spans: along the line (`dy` between the same averaged
             * neighbours) and across it (`c`). Both were hard-coded to
             * straight up, which is fine on a floor and wrong on every
             * traverse — a ribbon lying on a 25° hillside was lit as though it
             * faced the sky, so a path across a shaded slope was the brightest
             * thing on it. `normalize(side × tangent)` with y up gives the
             * upward normal; the guard keeps a degenerate rung (two coincident
             * points, which `resample` avoids but a hand-built PathView can
             * still contain) pointing up rather than nowhere.
             */
            // n = s × t, with s = (px, c, pz) across and t = (tx, ty, tz) along
            const tx = dx / l, tz = dz / l, ty = (b.y - a.y) / l;
            const nx = c * tz - pz * ty;
            // pz·tx − px·tz is (dx² + dz²)/l², i.e. exactly 1 for any rung
            // whose two neighbours differ — which is why the guard below only
            // ever fires on a coincident pair
            const ny = pz * tx - px * tz;
            const nz = px * ty - c * tx;
            const nl = Math.hypot(nx, ny, nz);
            if (nl > 1e-6 && ny > 0) {
              nrm[k] = nx / nl; nrm[k + 1] = ny / nl; nrm[k + 2] = nz / nl;
              nrm[k + 3] = nx / nl; nrm[k + 4] = ny / nl; nrm[k + 5] = nz / nl;
            } else {
              nrm[k + 1] = 1; nrm[k + 4] = 1;
            }
          }
          for (let i = 0; i < P - 1; i++) {
            const q = base + i * 2;
            idx.push(q, q + 2, q + 1, q + 1, q + 2, q + 3);
          }
          base += P * 2;
        }

        const geo = new THREE.BufferGeometry();
        geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
        geo.setAttribute('normal', new THREE.BufferAttribute(nrm, 3));
        geo.setAttribute('aSide', new THREE.BufferAttribute(side, 2));
        geo.setAttribute('aCross', new THREE.BufferAttribute(cross, 1));
        geo.setAttribute('aEdge', new THREE.BufferAttribute(edge, 1));
        geo.setAttribute('aStrength', new THREE.BufferAttribute(strengths, 1));
        geo.setIndex(idx);
        geo.computeBoundingSphere();
        mesh.geometry = geo;
        mesh.visible = true;
      }

      // strength decays on the sim's day cadence with no version bump — sync
      // it every call so a dying path narrows and dims before it dissolves
      if (!strengths) return;
      const attr = mesh.geometry.getAttribute('aStrength') as THREE.BufferAttribute | undefined;
      if (!attr) return;
      let moved = false;
      for (const v of views) {
        const s = span.get(v.id);
        if (!s) continue;
        // compare at the precision the attribute actually stores, or every
        // strength that is not exactly a float32 re-uploads the whole network
        // every frame for ever
        const w = Math.fround(v.strength);
        for (let i = 0; i < s.n; i++) {
          if (strengths[s.at + i] === w) continue;
          strengths[s.at + i] = w;
          moved = true;
        }
      }
      if (moved) attr.needsUpdate = true;
    },
  };
}

/** Exported for the tests, and for anyone measuring what a strength looks like. */
export { widthOf as pathWidth, opacityOf as pathOpacity };
